import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { TLinkGroup } from '@ingeniorforeningen/figurine-core/build/application/Header/parts/LocalNavbar';
import { setHeaderLocalLinks } from '../helpers/navigationHelpers';
import { getTexts, getCases, getPromotions } from '../api';
import {
  getCategoryName,
  getEmploymentTypeName,
  getSubCategoryName,
} from '../helpers/casesHelpers';
import {
  AppLanguage,
  Cases,
  Texts,
  Promotions,
  CaseMessage,
  CaseDetails,
  CaseDocument,
} from '../types';
import {
  getCaseDocuments,
  getDetails,
  getMessages,
  postMessages,
} from '../api/services/caseDetails';

type TAppStore = {
  appLanguage: AppLanguage;
  headerLocalLinks: TLinkGroup[];
  appTexts: { loading: boolean; error: string | null; data: Texts };
  cases: { loading: boolean; error: string | null; data: Cases | null };
  caseMessages: { loading: boolean; error: string | null; data: CaseMessage[] | null };
  caseDocuments: { loading: boolean; error: string | null; data: CaseDocument[] | null };
  caseDetails: {
    loading: boolean;
    error: string | null;
    data: CaseDetails | null;
    isCaseDetailsPopulated: boolean;
  };
  promotions: { loading: boolean; error: string | null; data: Promotions | null };
  postingCaseMessage: { isPosting: boolean; error: string | null; isPosted: boolean };
  setAppLanguage: (lang: AppLanguage) => void;
  fetchTexts: () => Promise<void>;
  fetchCases: () => Promise<void>;
  fetchPromotions: (lang: AppLanguage, subjectId: string) => Promise<void>;
  fetchCaseMessages: (caseId: string) => Promise<void>;
  fetchCaseDocuments: (caseId: string) => Promise<void>;
  fetchCaseDetails: (caseId: string) => Promise<void>;
  postCaseMessage: (caseId: string, message: string, documents: string[]) => Promise<void>;
  updateCaseDetails: (texts: Texts, caseDetails: CaseDetails) => void;
};

const initialState = {
  appLanguage: AppLanguage.Danish,
  headerLocalLinks: [],
  appTexts: { loading: true, error: null, data: {} },
  cases: { loading: true, error: null, data: null },
  caseMessages: { loading: true, error: null, data: null },
  caseDocuments: { loading: true, error: null, data: null },
  caseDetails: { loading: true, error: null, data: null, isCaseDetailsPopulated: false },
  promotions: { loading: true, error: null, data: null },
  postingCaseMessage: { isPosting: false, error: null, isPosted: false },
};

const useAppStore = create<TAppStore>()(
  devtools(
    immer((set, get) => ({
      appLanguage: initialState.appLanguage,
      headerLocalLinks: initialState.headerLocalLinks,
      appTexts: initialState.appTexts,
      cases: initialState.cases,
      caseMessages: initialState.caseMessages,
      caseDocuments: initialState.caseDocuments,
      caseDetails: initialState.caseDetails,
      promotions: initialState.promotions,
      postingCaseMessage: initialState.postingCaseMessage,
      setAppLanguage: (lang) =>
        set((state) => {
          state.appLanguage = lang;
        }),
      fetchTexts: async () => {
        set((state) => {
          state.appTexts = initialState.appTexts;
        });

        try {
          const response = await getTexts(get().appLanguage);
          const [
            globalContentResponse,
            caseOverviewResponse,
            caseDetailsResponse,
            teamShareResponse,
          ] = response;
          if (
            globalContentResponse.status === 200 &&
            caseOverviewResponse.status === 200 &&
            caseDetailsResponse.status === 200 &&
            teamShareResponse.status === 200
          ) {
            set((state) => {
              state.appTexts.data.globalContentTexts = globalContentResponse.data.globalContent;
              state.headerLocalLinks = setHeaderLocalLinks(
                globalContentResponse.data.globalContent?.headerLocalLinks,
              );
              state.appTexts.data.caseOverviewTexts = caseOverviewResponse.data.caseOverviewTexts;
              state.appTexts.data.caseDetailsTexts = caseDetailsResponse.data.caseDetailsTexts;
              state.appTexts.data.teamShareTexts = teamShareResponse.data;
            });
          }
        } catch (error: any) {
          set((state) => {
            state.appTexts.error = error.message;
          });
        } finally {
          set((state) => {
            state.appTexts.loading = false;
          });
        }
      },
      fetchCases: async () => {
        set((state) => {
          state.cases = initialState.cases;
        });
        try {
          const response = await getCases();
          if (response && response.data) {
            const cases = response.data.cases.sort((x, y) =>
              x.creationDate < y.creationDate ? 1 : -1,
            );

            set((state) => {
              state.cases.data = { cases: cases };
            });
          }
        } catch (error: any) {
          set((state) => {
            state.cases.error = error.message;
          });
        } finally {
          set((state) => {
            state.cases.loading = false;
          });
        }
      },
      fetchPromotions: async (lang, subjectId) => {
        set((state) => {
          state.promotions = initialState.promotions;
        });
        try {
          const response = await getPromotions(lang, subjectId);
          if (response && response.data) {
            set((state) => {
              state.promotions.data = response.data;
            });
          }
        } catch (error: any) {
          set((state) => {
            state.promotions.error = error.message;
          });
        } finally {
          set((state) => {
            state.promotions.loading = false;
          });
        }
      },
      fetchCaseMessages: async (caseId) => {
        set((state) => {
          state.caseMessages = initialState.caseMessages;
        });
        try {
          const response = await getMessages(caseId);
          if (response && response.data) {
            set((state) => {
              state.caseMessages.data = response.data.notes.reverse();
            });
          }
        } catch (error: any) {
          set((state) => {
            state.caseMessages.error = error.message;
          });
        } finally {
          set((state) => {
            state.caseMessages.loading = false;
          });
        }
      },
      fetchCaseDocuments: async (caseId) => {
        set((state) => {
          state.caseDocuments = initialState.caseDocuments;
        });
        try {
          const response = await getCaseDocuments(caseId);
          if (response && response.data) {
            set((state) => {
              state.caseDocuments.data = response.data.documents;
            });
          }
        } catch (error: any) {
          set((state) => {
            state.caseDocuments.error = error.message;
          });
        } finally {
          set((state) => {
            state.caseDocuments.loading = false;
          });
        }
      },
      postCaseMessage: async (caseId, message, documents) => {
        set((state) => {
          state.postingCaseMessage = { isPosting: true, error: null, isPosted: false };
        });
        try {
          const response = await postMessages(caseId, message, documents);
          if (response.status === 201 || response.status === 202) {
            set((state) => {
              state.postingCaseMessage.isPosted = true;
            });
          }
        } catch (error: any) {
          set((state) => {
            state.postingCaseMessage.error = error.message;
          });
        } finally {
          set((state) => {
            state.postingCaseMessage.isPosting = false;
          });
        }
      },
      fetchCaseDetails: async (caseId) => {
        set((state) => {
          state.caseDetails = initialState.caseDetails;
        });
        try {
          const response = await getDetails(caseId);
          if (response && response.data) {
            const data = response.data as CaseDetails;
            set((state) => {
              state.caseDetails.data = data;
            });
          }
        } catch (error: any) {
          set((state) => {
            state.caseDetails.error = error.message;
          });
        } finally {
          set((state) => {
            state.caseDetails.loading = false;
          });
        }
      },
      updateCaseDetails: (texts, caseDetails) => {
        set((state) => {
          state.caseDetails.data!.categoryName = getCategoryName(texts, caseDetails.categoryAlias);
          state.caseDetails.data!.subCategoryName = getSubCategoryName(
            texts,
            caseDetails.subcategoryAlias,
          );
          state.caseDetails.data!.employmentTypeName = getEmploymentTypeName(
            texts,
            caseDetails.employmentType,
          );
          state.caseDetails.isCaseDetailsPopulated = true;
        });
      },
    })),
    { name: 'CaseOverviewStore' },
  ),
);

export default useAppStore;
